import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

const sizes = { sm: "max-w-[420px]", md: "max-w-[540px]", lg: "max-w-[720px]", xl: "max-w-[960px]" };

/** Oyna (redesign v2): orqa fon xiralashadi, 20px radius, 160 ms kirish; Esc va tashqariga bosish yopadi */
export function Modal({ open, onOpenChange, title, description, children, footer, size = "md" }: ModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fade-in fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] dark:bg-black/60" />
        <Dialog.Content
          className={cn(
            "pop-in-center fixed top-1/2 left-1/2 z-50 w-[calc(100%-32px)] -translate-x-1/2 -translate-y-1/2 rounded-card border border-border/60 bg-surface shadow-menu focus:outline-none",
            sizes[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-5">
            <div className="min-w-0">
              <Dialog.Title className="text-[18px] leading-snug font-semibold tracking-[-0.01em]">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-[13px] text-muted">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{typeof title === "string" ? title : "Dialog"}</Dialog.Description>
              )}
            </div>
            <Dialog.Close className="-mt-1 -mr-2 grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-fill hover:text-ink" aria-label={t("actions.close")}>
              <X size={16} />
            </Dialog.Close>
          </div>
          <div className="px-6 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
